import { __extends } from "tslib";
import { BitmapFilter } from './BitmapFilter';
import { NumberUtilities, isNullOrUndefined, toNumber } from '@awayfl/swf-loader';
import { Errors } from '@awayfl/avm2';
/**
 * Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// Class: ConvolutionFilter
var ConvolutionFilter = /** @class */ (function (_super) {
    __extends(ConvolutionFilter, _super);
    function ConvolutionFilter(matrixX, matrixY, matrix, divisor, bias, preserveAlpha, clamp, color, alpha) {
        if (matrixX === void 0) { matrixX = 0; }
        if (matrixY === void 0) { matrixY = 0; }
        if (matrix === void 0) { matrix = null; }
        if (divisor === void 0) { divisor = 1; }
        if (bias === void 0) { bias = 0; }
        if (preserveAlpha === void 0) { preserveAlpha = true; }
        if (clamp === void 0) { clamp = true; }
        if (color === void 0) { color = 0; }
        if (alpha === void 0) { alpha = 0; }
        var _this = _super.call(this) || this;
        _this.matrixX = matrixX;
        _this.matrixY = matrixY;
        if (matrix) {
            _this.matrix = matrix;
        }
        else {
            _this._matrix = _this._expandArray([], _this._matrixX * _this._matrixY);
        }
        _this.divisor = divisor;
        _this.bias = bias;
        _this.preserveAlpha = preserveAlpha;
        _this.clamp = clamp;
        _this.color = color;
        _this.alpha = alpha;
        return _this;
    }
    ConvolutionFilter.FromUntyped = function (obj) {
        return new ConvolutionFilter(obj.matrixX, obj.matrixY, obj.matrix, obj.divisor, obj.bias, obj.preserveAlpha, obj.clamp, 
        // obj.color is an RGBA color.
        obj.color >>> 8, (obj.color & 0xff) / 0xff);
    };
    ConvolutionFilter.prototype._expandArray = function (a, newLen /*uint*/, value) {
        if (value === void 0) { value = 0; }
        if (a) {
            var i = a.length;
            while (i < newLen) {
                a[i++] = 0;
            }
        }
        return a;
    };
    Object.defineProperty(ConvolutionFilter.prototype, "matrix", {
        get: function () {
            return this.sec.createArrayUnsafe(this._matrix.slice(0, this._matrixX * this._matrixY));
        },
        set: function (value_) {
            if (isNullOrUndefined(value_)) {
                this.sec.throwError('TypeError', Errors.NullPointerError, 'matrix');
            }
            var value = value_.value;
            var actualLen = this._matrixX * this._matrixY;
            var minLen = Math.min(value.length, actualLen);
            var matrix = Array(minLen);
            for (var i = 0; i < minLen; i++) {
                matrix[i] = toNumber(value[i]);
            }
            this._expandArray(matrix, actualLen);
            this._matrix = matrix;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConvolutionFilter.prototype, "matrixX", {
        get: function () {
            return this._matrixX;
        },
        set: function (value) {
            var mx = NumberUtilities.clamp(+value, 0, 15) | 0;
            if (this._matrixX !== mx) {
                this._matrixX = mx;
                this._expandArray(this._matrix, mx * this._matrixY);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConvolutionFilter.prototype, "matrixY", {
        get: function () {
            return this._matrixY;
        },
        set: function (value) {
            var my = NumberUtilities.clamp(+value, 0, 15) | 0;
            if (this._matrixY !== my) {
                this._matrixY = my;
                this._expandArray(this._matrix, my * this._matrixX);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConvolutionFilter.prototype, "divisor", {
        get: function () {
            return this._divisor;
        },
        set: function (value) {
            this._divisor = +value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConvolutionFilter.prototype, "bias", {
        get: function () {
            return this._bias;
        },
        set: function (value) {
            this._bias = +value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConvolutionFilter.prototype, "preserveAlpha", {
        get: function () {
            return this._preserveAlpha;
        },
        set: function (value) {
            this._preserveAlpha = !!value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConvolutionFilter.prototype, "clamp", {
        get: function () {
            return this._clamp;
        },
        set: function (value) {
            this._clamp = !!value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConvolutionFilter.prototype, "color", {
        get: function () {
            return this._color;
        },
        set: function (value /*uint*/) {
            this._color = value >>> 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConvolutionFilter.prototype, "alpha", {
        get: function () {
            return this._alpha;
        },
        set: function (value) {
            this._alpha = NumberUtilities.clamp(+value, 0, 1);
        },
        enumerable: true,
        configurable: true
    });
    ConvolutionFilter.prototype.clone = function () {
        return new ConvolutionFilter(this._matrixX, this._matrixY, this.matrix, this._divisor, this._bias, this._preserveAlpha, this._clamp, this._color, this._alpha);
    };
    ConvolutionFilter.classInitializer = null;
    return ConvolutionFilter;
}(BitmapFilter));
export { ConvolutionFilter };
